import type { PageMetadata } from "generated/schemas";
import type { PaginatedResponse } from "types/pagination";

/**
 * Shape of every Spring Data `PagedModel` response after the backend switched
 * to `@EnableSpringDataWebSupport(pageSerializationMode = VIA_DTO)`.
 *
 * Only `size`, `number`, `totalElements`, `totalPages` are returned (inside
 * `page`). The legacy flat fields (`last`, `first`, `numberOfElements`,
 * `empty`, `pageable`, `sort`) are no longer sent and must be derived here.
 */
export type PagedModelLike<T> = {
  content?: T[];
  page?: PageMetadata;
};

export type NormalizedPageMetadata = {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
};

/**
 * Normalize a `PageMetadata` block into the values the UI needs, deriving the
 * fields the backend no longer serializes (`last`, `first`).
 *
 * `contentLength` is used as a sensible fallback for `size`/`totalElements`
 * when the metadata is absent (e.g. a non-paged endpoint).
 */
export const normalizePageMetadata = (
  page: PageMetadata | undefined,
  contentLength: number,
): NormalizedPageMetadata => {
  const number = page?.number ?? 0;
  const totalPages = page?.totalPages ?? 0;

  return {
    size: page?.size ?? contentLength,
    number,
    totalElements: page?.totalElements ?? contentLength,
    totalPages,
    last: totalPages === 0 || number + 1 >= totalPages,
    first: number === 0,
  };
};

/**
 * Map a `PagedModel` response to the internal `PaginatedResponse` shape.
 */
export const mapPage = <TIn, TOut>(
  dto: PagedModelLike<TIn>,
  mapItem: (item: TIn) => TOut,
): PaginatedResponse<TOut> => {
  const content = dto.content ?? [];
  const meta = normalizePageMetadata(dto.page, content.length);

  return {
    items: content.map(mapItem),
    totalItems: meta.totalElements,
    size: meta.size,
    page: meta.number,
    totalPages: meta.totalPages,
    last: meta.last,
  };
};
